import {
  Button,
  Chip,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import { useToggle } from "../../../../Utils/Hooks/useToggle";

import CustomerEditUserPanel from "../../../../Components/Panels/CustomerEditUserPanel/customer-edit-user-panel";
import { ModalContext } from "../../../../Components/Modals";
import ModalAddUser, { ModalAddUserElementProps } from "../../../../Components/Modals/ModalAddUser/modal-add-user";

import { useOutletContext } from "react-router";
import { SettingsOutletContextType } from "../settings";
import { GridRowsProp, GridRowParams, GridActionsCellItem, GridColDef } from "@mui/x-data-grid";
import DataTable, { CellSize } from "../../../../Components/Table/table";
import TableSkeleton from "../../../../Components/Table/TableSkeleton";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { userService } from "../../../../Core/Api/services";
import { DbUserGroupInfos } from "../../../../Core/Api/Types/db-user-group-infos";
import TableCellName from "../../../../Components/Table/TableCellName";
import ListPageHeader, {
  ListPageHeaderGroupActions,
  ListPageHeaderRight,
} from "../../../../Components/Misc/ListPageHeader";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import { SetUsersList } from "../../../../Redux/Reducers/redux-slice-settings-users";
import { useAppDispatch } from "../../../../Redux/redux-store";
import TableCellBool from "../../../../Components/Table/TableCellBool";
import TableCellDateTime from "../../../../Components/Table/TableCellDateTime/table-cell-date-time";
import CheckIcon from "@mui/icons-material/Check";
import { UserAdvancedInfos } from "../../../../Core/Api/Types/user-advanced-infos";
import TableCellChip from "../../../../Components/Table/TableCellChip/table-cell-chip";
import { UserHasAcl } from "../../../../Utils/helpers";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import UserGroupChip from "../../../../Components/Misc/Chips/UserGroupChip/user-group-chip";
import GlobalConst from "../../../../Core/Utils/Constants/globals";
import { AuthMode } from "../../../../Core/Api/Types/auth-mode";
import { DbUserGroupRoleInfos } from "../../../../Core/Api/Types/db-user-group-role-infos";
import TableCellTypography from "../../../../Components/Table/TableCellTypography/table-cell-typography";
import { saveAs } from "file-saver";
import { useTranslation } from "react-i18next";
import { ManageAccountsOutlined } from "@mui/icons-material";
import TableUuids from "../../../../Components/Table/table-uuids";

export interface UsersPermissionsRows {
  id: number;
  firstName: string | null;
  lastName: string | null;
  aclExceptions: number | null;
  enabled: boolean | null;
  isVerified: boolean | null;
  expireDate: string | null;
  groupId: number | null;
  roleId?: number | null;
  groupName: string;
  login: string | null;
  email: string | null;
  userAdvancedInfos: UserAdvancedInfos;
  customerLinksNames: (string | null)[];
  authMode: AuthMode;
}

export interface UsersPermissionsProps {
  rows: UsersPermissionsRows[];
  filtersData: {
    groups: (DbUserGroupInfos | undefined)[];
  };
}

function SettingsUsers() {
  const { t } = useTranslation();
  // Top filters
  const [filtersData, setFiltersData] = React.useState<UsersPermissionsProps["filtersData"]>({
    groups: [],
  });

  // Table filter
  const [textFilter, setTextFilter] = React.useState("");
  // Loading
  const [isLoading, setIsLoading] = React.useState(true);
  // Role filter
  const [roleFilter, setRoleFilter] = React.useState<string>("all");
  // Group filter
  const [groupFilter, setGroupFilter] = React.useState("");
  // Enabled filter
  const [enabledFilter, setEnabledFilter] = React.useState("Default");
  // Expire Date filter
  const [expireDateFilter, setExpireDateFilter] = React.useState("Default");

  const enabled = ["Enabled", "Disabled"];
  const expireDate = ["Expire date On", "Expire date Off"];

  const [userSelected, setUserSelected] = React.useState<UserAdvancedInfos>();
  const [customerEditUserPanelOpen, setCustomerEditUserPanelOpen] = useState(false);
  const [selectedItemIds, setSelectedItems] = React.useState<number[]>([]);
  const enqueueSnackbar = useCustomSnackbar();
  const { setSelectionMode, selectionMode } = useOutletContext<SettingsOutletContextType>();

  // Table rows
  const [rows, setRows] = React.useState<UsersPermissionsRows[]>([]);
  const [roles, setRoles] = React.useState<DbUserGroupRoleInfos[]>([]);
  const modalContext = React.useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const apiGrid = useGridApiRef();

  const handleNewUserClick = React.useCallback(() => {
    if (!openModal) {
      return;
    }

    const onClose: ModalAddUserElementProps["onClose"] = (data) => {};

    openModal<ModalAddUserElementProps>(ModalAddUser, { onClose });
  }, [openModal]);

  const handleChangeRoleSelect = (event: SelectChangeEvent) => {
    setRoleFilter(event.target.value);
  };

  // #region Grid definition
  let _rows: GridRowsProp = [];
  type Row = (typeof _rows)[number];

  const cols: GridColDef[] = React.useMemo(
    () => [
      {
        headerName: t("common:name"),
        field: "name",
        flex: CellSize.xlarge,
        minWidth: CellSize.xlarge,
        valueGetter: (data: Row, row: any) =>
          !row ? "" : `${row["firstName"] || ""} ${row["lastName"] || ""} ${row["id"] || 0}`,
        renderCell: (data: Row) => (
          <TableCellName
            data={{
              value: {
                firstname: data.row["firstName"] || "",
                lastname: data.row["lastName"] || "",
                id: data["id"] || 0,
              },
            }}
          />
        ),
      },
      {
        headerName: t("common:login"),
        field: "login",
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: Row) => <TableCellTypography data={data.value} />,
      },
      {
        headerName: t("common:email"),
        field: "email",
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: Row) => <TableCellTypography data={data.value} />,
      },
      {
        headerName: t("table:headerName.role"),
        field: "roleId",
        flex: CellSize.large,
        minWidth: CellSize.large,
        type: "singleSelect",
        valueOptions: roles.map((x) => x.name || "No role"),
        valueGetter: (data: Row, row: any) => roles.find((x) => x.id === row?.roleId)?.name || "No role",
        renderCell: (data: Row) => <UserGroupChip roleId={data?.row?.roleId} roles={roles} sx={{ mt: 0.5, mb: 0.5 }} />,
      },
      {
        headerName: t("common:group"),
        field: "groupName",
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: Row) => <Typography sx={{ wordBreak: "break-word" }}>{data.value}</Typography>,
      },
      {
        headerName: t("common:enabled"),
        field: "enabled",
        type: "boolean",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        headerName: t("common:activated"),
        field: "isVerified",
        type: "boolean",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data: Row) => <TableCellBool value={data.value} />,
      },
      {
        headerName: t("modal:body.customerLinks"),
        field: "customerLinksNames",
        valueGetter: (data: any, row: any) => row?.customerLinksNames?.join(" ") || "",
        flex: CellSize.large,
        minWidth: CellSize.large,
        renderCell: (data: Row) => <TableCellChip data={{ ...data, value: data?.row?.customerLinksNames }} />,
      },
      {
        headerName: t("common:expireDate"),
        field: "expireDate",
        type: "date",
        flex: CellSize.medium,
        minWidth: CellSize.medium,
        renderCell: (data: Row) => <TableCellDateTime data={{ ...data, value: data?.row?.expireDate }} />,
      },
      {
        headerName: t("table:headerName.auth"),
        field: "authMode",
        type: "singleSelect",
        valueOptions: Object.keys(AuthMode),
        flex: CellSize.small,
        minWidth: CellSize.small,
        renderCell: (data: Row) => <Chip label={data.value} />,
      },
      {
        headerName: "",
        type: "actions",
        align: "right",
        field: "actions",
        getActions: (params: GridRowParams) =>
          !params || !params.row
            ? []
            : [
                <GridActionsCellItem
                  icon={<ModeEditOutlinedIcon />}
                  label={t("common:editUser")}
                  onClick={() => {
                    setUserSelected(params?.row?.userAdvancedInfos);
                    setCustomerEditUserPanelOpen(true);
                  }}
                  showInMenu
                />,
                <GridActionsCellItem
                  icon={<DeleteOutlinedIcon color="error" />}
                  label={t("common:delete")}
                  color="error"
                  onClick={() =>
                    userService.DeleteUser(params.row["id"]).then(function (response) {
                      if (response.success) {
                        setRows(rows.filter((x) => x.id !== params.row["id"]));
                        enqueueSnackbar(t("alert:success.userSuccessfullyDeleted"), "success");
                      }
                    })
                  }
                  showInMenu
                />,
              ],
      },
    ],
    [filtersData, roles]
  );
  // #endregion

  // Redux
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    userService.GetUsersListForRolesAndPermissions().then(function (response) {
      if (response) {
        setFiltersData(response.filtersData);
        setRows(response.rows);
        dispatch(SetUsersList(response.rows.map((x) => x.userAdvancedInfos)));
      }
      setIsLoading(false);
    });

    userService.GetGroupRolesList().then(function (response) {
      if (response.success) {
        setRoles(response.groupRoles || []);
      }
    });
  }, []);

  React.useEffect(() => {
    setSelectionMode({ type: "user", selection: selectedItemIds });
  }, [selectedItemIds]);

  React.useEffect(() => {
    if (selectionMode.selection.length === 0 && selectedItemIds.length !== 0) setSelectedItems([]);
  }, [selectionMode]);

  // Export
  const exportData = () => {
    const csvData = generateCsvData();
    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "groups_acls_export.csv");
  };

  const generateCsvData = () => {
    // Data
    let groups = filtersData.groups;
    let acls = Object.keys(AclTypes) as Array<keyof typeof AclTypes>;

    // CSV header
    const header = "Group,ACL Name,Active\n";

    // Rows for each group and each ACL
    const rows = groups.flatMap((group) => {
      return acls.map((acl) => {
        const isActive = group?.acls?.includes(AclTypes[acl]) ? "TRUE" : "FALSE";
        return `${group!.name},${acl},${isActive}`;
      });
    });

    return header + rows.join("\n");
  };

  const additionalFilters = (
    <>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        startAdornment={<ManageAccountsOutlined />}
        MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
        value={roleFilter}
        onChange={handleChangeRoleSelect}
      >
        <MenuItem value="all" selected>
          {t("menu:item.allRoles")}
        </MenuItem>
        <MenuItem value="" selected>
          {t("menu:item.noRole")}
        </MenuItem>
        {roles.map((role) => (
          <MenuItem value={role.id.toString()} key={role.id}>
            {role.name}
          </MenuItem>
        ))}
      </Select>
      <Select
        displayEmpty
        IconComponent={ExpandMoreRoundedIcon}
        value={groupFilter}
        startAdornment={<PeopleAltOutlinedIcon />}
        onChange={(e) => setGroupFilter(e.target.value)}
        MenuProps={{ PaperProps: { sx: { maxHeight: 400 } } }}
      >
        <MenuItem value="" key="gdefault" selected>
          {t("menu:item.allGroups")}
        </MenuItem>
        {filtersData.groups.length > 0 &&
          filtersData.groups.map((type) => (
            <MenuItem key={type?.id} value={type?.id}>
              {type?.name}
            </MenuItem>
          ))}
      </Select>
    </>
  );

  const additionalActions = (
    <>
      <Button onClick={exportData} variant="outlined" color="primary" startIcon={<FileDownloadOutlinedIcon />}>
        {t("button:exportGroupsACLs")}
      </Button>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleNewUserClick}
        disabled={!UserHasAcl(AclTypes.aclBackofficeUserCreate)}
      >
        {t("button:newUser")}
      </Button>
    </>
  );

  return (
    <>
      <DataTable
        loading={isLoading}
        tableKey={TableUuids.SETTINGS_USERS}
        columns={cols}
        rows={rows.filter(
          (row) =>
            (enabledFilter === "Default" ||
              (row.enabled === false && enabledFilter === "Disabled") ||
              (row.enabled === true && enabledFilter === "Enabled")) &&
            (expireDateFilter === "Default" ||
              (row.expireDate === null && expireDateFilter === "Expire date Off") ||
              (row.expireDate !== null && expireDateFilter === "Expire date On")) &&
            (groupFilter === "" || row.groupId?.toString() === groupFilter.toString()) &&
            (roleFilter === "all" ||
              (roleFilter === "" && row.roleId === null) ||
              row.roleId?.toString() === roleFilter)
        )}
        getRowHeight={() => "auto"}
        pagination={true}
        initialState={{
          sorting: {
            sortModel: [{ field: "name", sort: "asc" }],
          },
        }}
        additionalFilters={additionalFilters}
        additionalActions={additionalActions}
        initialColumnVisibility={{
          login: false,
          customerLinksNames: false,
          expireDate: false,
          authMode: false,
        }}
        selectedRows={selectedItemIds}
        setSelectedRows={setSelectedItems}
        onRowClick={(e) => {
          setUserSelected(e.row.userAdvancedInfos);
          if (!customerEditUserPanelOpen) setCustomerEditUserPanelOpen(true);
        }}
        checkboxSelection={true}
        disableRowSelectionOnClick
        apiRef={apiGrid}
      />

      <CustomerEditUserPanel
        open={customerEditUserPanelOpen}
        onClose={(e) => setCustomerEditUserPanelOpen(false)}
        user={userSelected}
      />
    </>
  );
}

export default SettingsUsers;
